'use client';

import { type ComponentProps, type ReactElement, forwardRef, useMemo } from 'react';
import type { ShopperSearch } from '@/scapi';
import type { SCAPIShopperBasketsV2Schemas } from '@/types/scapi';
import { Link } from '@/components/link';
import { RemoveItemButton } from 'src/components/buttons/remove-item-button';
import { WishlistIconButton } from '@/components/buttons/wishlist-icon-button';
import { ProductTileGenericPriceBar } from '@/components/product-utils/tile-price-display';
import { useProductPriceWeightDisplay } from '@/components/product-utils/use-product-price-weight-display';
import { formatCurrency } from '@/lib/currency';
import {
    createProductUrl,
    isCourseProduct,
    isEventOrCourseProduct,
    type EnrichedProductItem,
} from '@/lib/product-utils';
import { formatMultipackWeightText, getMultipackContentProduct, getMultipackPackQty } from '@/lib/multipack';
import { focusVisibleOutline } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';
import { getDisplayQuantity } from '@/lib/sold-by-weight';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useTranslation } from 'react-i18next';
import { ProductTileGenericLineQuantity } from './product-tile-cart-or-quick-add';
import { ProductTileImageCustom } from './product-tile-image-custom';
import GiftInformation from './gift-information';
import { OrderStatusBadge } from '../account/order-list-item';
import { formatEventStartDateForTile } from '@/lib/product-event-tile';

type SearchHit = ShopperSearch.schemas['ProductSearchHit'];

function enrichedProductToSearchHit(productItem: EnrichedProductItem): SearchHit {
    const productId = productItem.productId ?? productItem.id ?? '';
    const productName = productItem.productName ?? productItem.name;
    return {
        productId,
        productName: productName ?? undefined,
        imageGroups: productItem.imageGroups,
        c_url: productItem.c_url,
    };
}

export interface ProductTileCustomGenericCardProps extends ComponentProps<'div'> {
    productItem: EnrichedProductItem;
    bonusDiscountLineItems?: SCAPIShopperBasketsV2Schemas['BonusDiscountLineItem'][];
    maxBonusQuantity?: number;
    /** Optional edit / extra actions rendered below the quantity row (e.g. cart item edit). */
    // extraActions?: ReactNode;
    // deliveryActions?: ReactNode;
    basketControlsDisabled?: boolean;
    onlyDisplayInOrderDetails?: boolean;
    isCancelled?: boolean;
    isReturned?: boolean;
    showPrecedingPrice?: boolean;
    isGifted?: boolean;
    linkDisabled?: boolean;
}

function ProductTileCustomGenericCardInner(
    {
        productItem,
        bonusDiscountLineItems,
        maxBonusQuantity,
        isGifted = false,
        // extraActions,
        // deliveryActions,
        basketControlsDisabled = false,
        className,
        onlyDisplayInOrderDetails = false,
        isCancelled = false,
        isReturned = false,
        showPrecedingPrice = true,
        linkDisabled = false,
        ...rest
    }: ProductTileCustomGenericCardProps,
    ref: React.ForwardedRef<HTMLDivElement>
): ReactElement {
    const { currency } = useSite();
    const { t: tProduct, i18n } = useTranslation('product');
    const { t: tQuantity } = useTranslation('quantitySelector');

    const isBonusProduct = Boolean(productItem.bonusProductLineItem);
    const isChoiceBasedBonusProduct = useMemo(() => {
        if (!isBonusProduct || !productItem.bonusDiscountLineItemId || !bonusDiscountLineItems) {
            return false;
        }
        const matchingLineItem = bonusDiscountLineItems.find((item) => item.id === productItem.bonusDiscountLineItemId);
        return Boolean(matchingLineItem?.bonusProducts && matchingLineItem.bonusProducts.length > 0);
    }, [bonusDiscountLineItems, isBonusProduct, productItem.bonusDiscountLineItemId]);
    const isAutoBonusProduct = isBonusProduct && !isChoiceBasedBonusProduct;

    const productName = productItem.productName ?? 'Product Name';
    const brand = productItem.brand;
    const isEventOrCourse = isEventOrCourseProduct(productItem);
    const productTypeLabel = isEventOrCourse
        ? tProduct(isCourseProduct(productItem) ? 'eventCourse.typeCourse' : 'eventCourse.typeEvent')
        : undefined;
    const eventStartDate =
        productItem.c_eventStartDate instanceof Date
            ? productItem.c_eventStartDate.toISOString()
            : productItem.c_eventStartDate;
    const eventDate = isEventOrCourse ? formatEventStartDateForTile(eventStartDate, i18n.language) : undefined;
    const productUrl = createProductUrl(productItem.c_url || '#');
    const searchHit = enrichedProductToSearchHit(productItem);
    const currencyCode = currency ?? 'EUR';

    // Multipack cart lines: weight/€/um from unit child; pack qty from bundledProducts.
    const contentProduct = getMultipackContentProduct(productItem);
    const packQty = getMultipackPackQty(productItem);
    const display = useProductPriceWeightDisplay(contentProduct, currencyCode, {
        priceProduct: packQty > 1 ? productItem : undefined,
        packQty: packQty > 1 ? packQty : undefined,
    });
    const unitRow = {
        ...display.unitRow,
        weightText: formatMultipackWeightText(display.unitRow.weightText, packQty),
    };

    const lineList =
        display.isOnSale && display.listPrice != null && display.listPrice > display.currentPrice
            ? display.listPrice
            : null;

    const currentFormatted = formatCurrency(display.currentPrice, i18n.language, currencyCode);
    const lineListFormatted = lineList != null ? formatCurrency(lineList, i18n.language, currencyCode) : null;

    const showWishlist = !isBonusProduct && productItem.itemId && !onlyDisplayInOrderDetails;
    const showRemove = !isAutoBonusProduct && productItem.itemId && !onlyDisplayInOrderDetails;
    const quantityDisabled = basketControlsDisabled || isAutoBonusProduct;
    const isAvailable = productItem.inventory?.orderable === true;

    return (
        <div
            ref={ref}
            data-testid={`cart-product-line-${productItem.itemId}`}
            className={cn('relative flex w-full min-w-0 gap-2', className)}
            {...rest}>
            <div
                className={cn(
                    'relative w-28 shrink-0 self-stretch overflow-hidden max-h-44 rounded-lg bg-sand-95',
                    (isCancelled || isReturned) && 'opacity-50'
                )}>
                <ProductTileImageCustom variant="plp-horizontal" product={searchHit} pdpLink={!linkDisabled} />
            </div>

            <div className="flex min-w-0 flex-col gap-2 pt-1 flex-1">
                <div className="flex w-full min-w-0 items-start gap-2 flex-1">
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        {(productTypeLabel || brand) && (
                            <p className="w-full min-w-0 body-copy-s text-muted-foreground line-clamp-1">
                                {productTypeLabel || brand}
                            </p>
                        )}
                        <div className="flex min-w-0 items-start gap-2">
                            <h3 className="min-w-0 flex-1 ">
                                {isAvailable && !linkDisabled ? (
                                    <Link
                                        to={productUrl}
                                        className={cn(
                                            'body-copy-m-bold text-left hover:text-primary line-clamp-2',
                                            focusVisibleOutline
                                        )}>
                                        {productName}
                                    </Link>
                                ) : (
                                    <p className="body-copy-m-bold text-left">{productName}</p>
                                )}
                            </h3>
                        </div>
                        {eventDate && (
                            <p className="body-copy-m-bold mt-1 text-muted-foreground capitalize">{eventDate}</p>
                        )}
                    </div>
                    {showWishlist ? (
                        <div className="relative z-20 flex size-11 max-h-10 min-h-10 shrink-0 items-center justify-center rounded-[40px]">
                            <WishlistIconButton product={searchHit} className="size-6 text-[var(--neutral-20)]" />
                        </div>
                    ) : null}
                </div>

                {/* {deliveryActions ? <div className="w-full min-w-0">{deliveryActions}</div> : null} */}
                {onlyDisplayInOrderDetails && (
                    <div className="flex items-center gap-2">
                        <p className="body-copy-s text-muted-foreground">{tQuantity('quantity')}</p>
                        <p className="body-copy-s-bold text-muted-foreground">{getDisplayQuantity(productItem)}</p>
                    </div>
                )}
                {isCancelled ? (
                    <div>
                        <OrderStatusBadge status="cancelled" />
                    </div>
                ) : null}
                {isReturned ? (
                    <div>
                        <OrderStatusBadge status="returned" />
                    </div>
                ) : null}
                {!isGifted ? (
                    <GiftInformation email={productItem.c_giftRecipientEmail} message={productItem.c_giftMessage} />
                ) : null}
                <ProductTileGenericPriceBar
                    t={tProduct}
                    isOnSale={display.isOnSale}
                    listFormatted={lineListFormatted}
                    pricePct={display.pricePct}
                    unitRow={unitRow}
                    perUnitLine={display.perUnitLine}
                    currentFormatted={currentFormatted}
                    showPrecedingPrice={showPrecedingPrice}
                    isGift={isBonusProduct}
                    omnibus={display.omnibus}
                />

                {!isAutoBonusProduct && productItem.itemId && !onlyDisplayInOrderDetails ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <ProductTileGenericLineQuantity
                            line={productItem}
                            product={productItem}
                            productName={productName}
                            quantityLayout="horizontal"
                            disabled={quantityDisabled}
                            max={isBonusProduct ? maxBonusQuantity : undefined}
                        />
                        {showRemove && (
                            <RemoveItemButton
                                itemId={productItem.itemId}
                                analyticsItemSource={productItem}
                                className="h-auto p-0 text-xs font-bold leading-4 tracking-[0.24px] text-[var(--neutral-10)] underline hover:underline"
                            />
                        )}
                    </div>
                ) : null}

                {/* {extraActions ? <div className="w-full min-w-0">{extraActions}</div> : null} */}
            </div>
        </div>
    );
}

const ProductTileCustomGenericCard = forwardRef(ProductTileCustomGenericCardInner);

export { ProductTileCustomGenericCard };
